import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canEditFindings, daysRemaining, fmtDate } from "@/components/grc/shared";
import { EditFindingModal } from "@/components/grc/EditFindingModal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Pencil, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 25;
const PILLARS = Array.from({ length: 15 }, (_, i) => `P${i + 1}`);

export const SEVERITY_BADGE = {
  CRITICAL: "bg-red-600 text-white",
  MAJOR: "bg-amber-500 text-white",
  MINOR: "bg-yellow-400 text-yellow-950",
};

export const STATUS_BADGE = {
  Open: "bg-red-100 text-red-700 border border-red-200",
  InProgress: "bg-blue-100 text-blue-700 border border-blue-200",
  Closed: "bg-green-100 text-green-700 border border-green-200",
  AcceptedRisk: "bg-slate-200 text-slate-600 border border-slate-300",
};

const CHIPS = [
  { key: null, label: "All" },
  { key: "CRITICAL", label: "Critical", active: "bg-red-600 text-white border-red-600" },
  { key: "MAJOR", label: "Major", active: "bg-amber-500 text-white border-amber-500" },
  { key: "MINOR", label: "Minor", active: "bg-yellow-400 text-yellow-950 border-yellow-400" },
];

const truncate = (s, n = 60) => (s && s.length > n ? `${s.slice(0, n)}…` : s || "—");

export const FindingsRegister = ({ assessmentId }) => {
  const { user } = useAuth();
  const canEdit = canEditFindings(user?.role);
  const [findings, setFindings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState(null);
  const [pillar, setPillar] = useState("all");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);

  const fetchFindings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (severity) params.severity = severity;
      if (pillar !== "all") params.pillar = pillar;
      if (status !== "all") params.status = status;
      if (source !== "all") params.source = source;
      const { data } = await api.get(`/grc/${assessmentId}/findings`, { params });
      setFindings(data.findings || []);
    } catch {
      setFindings([]);
    } finally {
      setLoading(false);
    }
  }, [assessmentId, severity, pillar, status, source]);

  useEffect(() => {
    fetchFindings();
    setPage(1);
  }, [fetchFindings]);

  useEffect(() => {
    api.get("/grc/teams").then(({ data }) => setTeams(data.teams || [])).catch(() => setTeams([]));
  }, []);

  const totalPages = Math.max(1, Math.ceil(findings.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => findings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [findings, page]
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 space-y-4" data-testid="findings-register">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2" data-testid="findings-severity-chips">
          {CHIPS.map((c) => {
            const isActive = severity === c.key;
            return (
              <button
                key={c.label}
                data-testid={`findings-chip-${c.label.toLowerCase()}`}
                onClick={() => setSeverity(c.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  isActive
                    ? c.active || "bg-[#003366] text-white border-[#003366]"
                    : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Select value={pillar} onValueChange={setPillar}>
            <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="findings-pillar-filter">
              <SelectValue placeholder="Pillar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pillars</SelectItem>
              {PILLARS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="findings-source-filter">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="assessment">Assessment</SelectItem>
              <SelectItem value="ropa_engine">RoPA</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="findings-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="InProgress">In Progress</SelectItem>
              <SelectItem value="Closed">Closed</SelectItem>
              <SelectItem value="AcceptedRisk">Accepted Risk</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 rounded-md" />)}
        </div>
      ) : findings.length === 0 ? (
        <div className="py-14 text-center text-sm text-slate-500" data-testid="findings-empty-state">
          No findings match your filters
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table data-testid="findings-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Severity</TableHead>
                  <TableHead className="text-xs">Pillar</TableHead>
                  <TableHead className="text-xs min-w-[220px]">Title</TableHead>
                  <TableHead className="text-xs">DPDP Ref</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Owner</TableHead>
                  <TableHead className="text-xs">Target Date</TableHead>
                  {canEdit && <TableHead className="text-xs text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((f) => {
                  const days = daysRemaining(f.target_date);
                  return (
                    <TableRow key={f.finding_id} data-testid={`finding-row-${f.finding_id}`}>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${SEVERITY_BADGE[f.severity] || "bg-slate-200 text-slate-700"}`}>
                          {f.severity}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span className="px-2 py-0.5 rounded border border-slate-300 text-[11px] font-mono text-slate-600">
                            {f.pillar}
                          </span>
                          {f.source === "ropa_engine" && (
                            <span
                              data-testid={`finding-origin-ropa-${f.finding_id}`}
                              title="Raised by the RoPA gap engine"
                              className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#00897B]/10 text-[#00897B] border border-[#00897B]/30"
                            >
                              RoPA
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-800">
                        {f.title && f.title.length > 60 ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-default">{truncate(f.title)}</span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[360px] text-xs">{f.title}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          truncate(f.title)
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{f.dpdp_ref || "—"}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_BADGE[f.status] || "bg-slate-100 text-slate-600"}`}>
                          {f.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-700">{f.owner_team || "Unassigned"}</TableCell>
                      <TableCell className="text-xs">
                        {f.target_date ? (
                          <div>
                            <div className="text-slate-700">{fmtDate(f.target_date)}</div>
                            <div className={`text-[10px] ${days !== null && days < 0 ? "text-red-600 font-semibold" : "text-slate-400"}`}>
                              {days === null ? "" : days < 0 ? `${Math.abs(days)}d overdue` : `${days}d remaining`}
                            </div>
                          </div>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-[#008080] hover:text-[#1a51db]"
                            onClick={() => setEditing(f)}
                            data-testid={`finding-edit-button-${f.finding_id}`}
                          >
                            <Pencil size={13} className="mr-1" /> Edit
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-xs text-slate-500" data-testid="findings-pagination">
            <span>
              {findings.length} finding{findings.length === 1 ? "" : "s"} · page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-7 px-2" disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)} data-testid="findings-prev-page">
                <ChevronLeft size={14} />
              </Button>
              <Button variant="outline" size="sm" className="h-7 px-2" disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)} data-testid="findings-next-page">
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </>
      )}

      <EditFindingModal
        finding={editing}
        teams={teams}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          fetchFindings();
        }}
      />
    </div>
  );
};
